export interface Project {
  title: string;
  slug: string;
  category: string;
  type: string;
  image: string;
  highlight: string;
  description: string;
  tech: string[];
  metrics: Record<string, string>;
  outcome: string;
}

export const projects: Project[] = [
  {
    title: 'One+ AI Music Studio',
    slug: 'oneplus-ai-music-studio',
    category: 'Artificial Intelligence',
    type: 'AI & Music Generation',
    image: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?q=80&w=1024',
    highlight: 'Promoted by Tanmay Bhat, Varun Mayya, etc.',
    description:
      'Created an AI tool that generates songs (audio + video) using a simple prompt. The project was promoted by notable personalities including Tanmay Bhat and Varun Mayya.',
    tech: ['AI', 'Music Generation', 'Video Synthesis', 'Prompt Engineering'],
    metrics: {
      'Demo Reel': 'https://www.instagram.com/thevarunmayya/reel/C1FBoPPrUwv/',
    },
    outcome:
      'Enabled users to create original songs and music videos using AI, making music production accessible to everyone. Widely recognized and promoted by influencers.',
  },
  {
    title: 'MTalkz',
    slug: 'mtalkz',
    category: 'Enterprise Solutions',
    type: 'Web Development',
    image: '/images/clientLogos/mtalkz.png',
    highlight: 'Modernized UI/UX, Speed, SEO & Security Boost',
    description:
      'Migrated MTalkz website from WordPress to Strapi + Next.js, delivering a modern UI/UX and significantly improving site speed, SEO, and security.',
    tech: ['Strapi', 'Next.js', 'React', 'Node.js', 'Vercel'],
    metrics: {
      'Website Speed': '2x faster',
      'SEO Score': 'Improved by 30%',
      Security: 'Hardened',
      Platform: 'www.mtalkz.com',
    },
    outcome:
      'Enabled MTalkz to offer a seamless, secure, and high-performing web experience, resulting in better engagement and discoverability.',
  },
  {
    title: 'TajDecor - AI-Enhanced Decor Platform',
    slug: 'tajdecor-platform',
    category: 'E-commerce',
    type: 'AI & E-commerce',
    image:
      'https://pollinations.ai/p/A%20luxury%20home%20decor%20platform%20with%20AI-enhanced%20image%20processing?seed=tajdecor&nologo=true',
    highlight: 'AI-Driven Image Enhancement',
    description:
      'Created a sophisticated e-commerce platform integrating AI-powered image placement and enhancement capabilities with Shopify and Next.js.',
    tech: ['Next.js', 'Shopify', 'AI Image Processing', 'TypeScript', 'TensorFlow.js'],
    metrics: {
      Platform: 'www.tajdecor.com',
      'Image Processing Time': '<2s',
      'Enhancement Quality': '99% accuracy',
    },
    outcome:
      'Revolutionized the home decor shopping experience with AI-powered image enhancement and intelligent product placement suggestions.',
  },
  {
    title: 'PetVesta - Pet Care E-commerce',
    slug: 'petvesta-store',
    category: 'E-commerce',
    type: 'Web Development',
    image:
      'https://pollinations.ai/p/A%20modern%20pet%20care%20e-commerce%20store%20with%20products%20and%20accessories?seed=petvesta&nologo=true',
    highlight: 'Custom Shopify Integration',
    description:
      'Developed a custom Shopify theme with seamless backend integration, synchronizing inventory and orders with existing systems and mobile app.',
    tech: ['Shopify', 'Liquid', 'Node.js', 'REST API', 'React Native'],
    metrics: {
      Platform: 'petvesta.myshopify.com',
      'Integration Points': 'Mobile App, Legacy Backend',
      'Sync Latency': '<500ms',
    },
    outcome:
      'Successfully unified e-commerce operations across web and mobile platforms while maintaining real-time synchronization with legacy systems.',
  },
  {
    title: 'Loan Doctor - Financial Services Platform',
    slug: 'loan-doctor-app',
    category: 'FinTech',
    type: 'Full-Stack Development',
    image:
      'https://pollinations.ai/p/A%20modern%20fintech%20loan%20application%20platform%20with%20mobile%20and%20web%20interface?seed=loandoctor&nologo=true',
    highlight: 'Cross-Platform Loan Management Solution',
    description:
      'Built a comprehensive loan management platform with PWA support, native mobile apps, and an admin dashboard for efficient loan processing and management.',
    tech: ['Next.js', 'Node.js', 'React Native', 'PWA', 'MongoDB'],
    metrics: {
      Platform: 'loan-doctor-app-one.vercel.app',
      'Supported Platforms': 'Web, iOS, Android',
      'Response Time': '<200ms',
    },
    outcome:
      'Delivered a unified loan management solution that streamlines the loan application process across multiple platforms.',
  },
  {
    title: 'Amriti - Ayurvedic Wellness Store',
    slug: 'amriti-wellness-store',
    category: 'E-commerce',
    type: 'Web Development',
    image:
      'https://pollinations.ai/p/A%20modern%20ayurvedic%20wellness%20store%20with%20elegant%20design%20and%20natural%20products?seed=amriti&nologo=true',
    highlight: 'Custom Shopify + Next.js Integration',
    description:
      "Developed a modern e-commerce platform for Amriti, combining Shopify's robust backend with a custom Next.js frontend to deliver an exceptional shopping experience for Ayurvedic wellness products.",
    tech: ['Shopify', 'Next.js', 'TypeScript', 'Tailwind CSS', 'Shopify API'],
    metrics: {
      Platform: 'www.amriti.store',
      'Page Load Time': '<1s',
      'Mobile Optimization': '95%+ score',
    },
    outcome:
      'Created a seamless, high-performance e-commerce experience that perfectly balances modern technology with traditional Ayurvedic values.',
  },
  {
    title: 'Custom AI Agent for Website Visitors',
    slug: 'custom-ai-agent-for-website-visitors',
    category: 'Artificial Intelligence',
    type: 'AI & Machine Learning',
    image:
      'https://pollinations.ai/p/Developed%20an%20intelligent%20AI%20chat%20agent%20using%20RAG%20(Retrieval%20Augmented%20Generation)%20technology%20to%20assist%20website%20visitors%20with%20personalized%20information%20and%20support.?seed=550176021&nologo=true&model=flux',
    highlight: '45% Increase in Lead Conversion',
    description:
      'Developed an intelligent AI chat agent using RAG (Retrieval Augmented Generation) technology to assist website visitors with personalized information and support.',
    tech: ['Next.js', 'TypeScript', 'LangChain', 'Dialogflow', 'Pinecone'],
    metrics: {
      'Lead Conversion Rate': '45% increase',
      'Query Response Time': '<500ms',
      'Information Accuracy': '93%',
    },
    outcome:
      'Enhanced user engagement by providing contextually relevant information, resulting in a 45% increase in lead conversion rates.',
  },
  {
    title: 'AI-Powered Analytics Platform',
    slug: 'ai-powered-analytics-platform',
    category: 'Artificial Intelligence',
    type: 'AI & Machine Learning',
    image: 'https://images.unsplash.com/photo-1518186285589-2f7649de83e0?q=80&w=1024',
    highlight: '500M+ Data Points Processed Daily',
    description:
      'Developed a real-time analytics platform leveraging advanced machine learning models to process and analyze over 500 million data points daily.',
    tech: ['React', 'Python', 'TensorFlow', 'AWS'],
    metrics: {
      'Data Points Processed Daily': '500M+',
      'Real-Time Processing Speed': '<100ms per request',
      'Model Accuracy': '99.7%',
    },
    outcome:
      'Achieved a 90% reduction in processing time and increased data analysis accuracy by 15%, providing actionable insights to enterprise clients.',
  },
  {
    title: 'Enterprise Cloud Migration',
    slug: 'enterprise-cloud-migration',
    category: 'Cloud Infrastructure',
    type: 'Enterprise Solutions',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=1024',
    highlight: '99.99% Uptime Achievement',
    description:
      'Executed a seamless migration of 200+ legacy systems to a modern cloud infrastructure for a Fortune 500 company, ensuring scalability and resilience.',
    tech: ['AWS', 'Terraform', 'Docker', 'Kubernetes'],
    metrics: {
      'Systems Migrated': '200+',
      'Platform Uptime': '99.99%',
      'Operational Cost Reduction': '40%',
    },
    outcome:
      'Delivered a zero-downtime migration while reducing operational expenses by 40%, significantly enhancing system reliability.',
  },
  {
    title: 'DeFi Trading Protocol',
    slug: 'defi-trading-protocol',
    category: 'Blockchain & Web3',
    type: 'Blockchain & Web3',
    image: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?q=80&w=1024',
    highlight: '$100M+ Total Value Locked',
    description:
      'Created a decentralized trading protocol that supports advanced liquidity pools, yield farming, and flash loan protection.',
    tech: ['Solidity', 'Web3.js', 'React', 'Node.js'],
    metrics: {
      'Total Value Locked (TVL)': '$120M+',
      'Transactions Processed': '2M+',
      'Gas Cost Reduction': '30%',
    },
    outcome:
      'Achieved $120M+ in TVL within six months, providing users with efficient and secure trading experiences.',
  },
  {
    title: 'Personalized Recommendation Engine',
    slug: 'personalized-recommendation-engine',
    category: 'Artificial Intelligence',
    type: 'AI & Machine Learning',
    image: 'https://images.unsplash.com/photo-1509021436665-8f07dbf5bf1d?q=80&w=1024',
    highlight: '95% Recommendation Accuracy',
    description:
      'Built a recommendation engine using deep learning to provide hyper-personalized suggestions for e-commerce and streaming platforms.',
    tech: ['Python', 'Keras', 'AWS Lambda', 'MongoDB'],
    metrics: {
      'Recommendation Accuracy': '95%',
      'User Retention Increase': '20%',
      'Processing Time': '<200ms',
    },
    outcome:
      'Enhanced user retention by 20% and improved customer satisfaction with real-time personalized recommendations.',
  },
  {
    title: 'Quantum Trading System',
    slug: 'quantum-trading-system',
    category: 'Advanced Computing',
    type: 'AI & Machine Learning',
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=1024',
    highlight: '10x Performance Improvement',
    description:
      'Designed a quantum-inspired trading algorithm for high-frequency trading, optimizing portfolio performance using advanced computational principles.',
    tech: ['Qiskit', 'Python', 'CUDA', 'C++'],
    metrics: {
      'Performance Improvement': '10x over legacy systems',
      Latency: '<1ms per transaction',
      'Algorithm Success Rate': '94%',
    },
    outcome:
      'Achieved a tenfold performance boost, positioning the trading system as a leader in high-frequency markets.',
  },
  {
    title: 'Astrology AI Chatbot',
    slug: 'astrology-ai-chatbot',
    category: 'Artificial Intelligence',
    type: 'AI & Machine Learning',
    image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?q=80&w=1024',
    highlight: '85% User Engagement',
    description:
      'Developed Oryn.Chat, an astrology AI chatbot offering personalized horoscope readings, compatibility analysis, and guidance based on planetary positions.',
    tech: ['Next.js', 'TypeScript', 'OpenAI', 'Tailwind CSS'],
    metrics: {
      'User Engagement': '85%',
      'Daily Active Users': '10K+',
      'Response Time': '<1s',
    },
    outcome:
      'Successfully launched a scalable chatbot with high user engagement and accurate astrology insights.',
  },
  {
    title: 'AI-Powered Customer Support System',
    slug: 'ai-powered-customer-support-system',
    category: 'Artificial Intelligence',
    type: 'AI & Machine Learning',
    image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?q=80&w=1024',
    highlight: '40% Resolution Time Reduction',
    description:
      'Implemented an AI customer support assistant to automate ticket handling and improve resolution times.',
    tech: ['Python', 'Dialogflow', 'MongoDB', 'Docker'],
    metrics: {
      'Resolution Time Reduction': '40%',
      'Customer Satisfaction': '92%',
      'Tickets Automated': '100K+',
    },
    outcome:
      'Increased customer satisfaction by 20% while automating a significant portion of support tickets.',
  },
  {
    title: 'Custom ERP Solution',
    slug: 'custom-erp-solution',
    category: 'Enterprise Software',
    type: 'Enterprise Solutions',
    image: '/images/CustomERPSolution.jpeg',
    highlight: '30% Process Efficiency Increase',
    description:
      'Developed a scalable ERP system tailored for mid-sized manufacturing enterprises, improving workflow automation and inventory management.',
    tech: ['Node.js', 'Angular', 'PostgreSQL', 'Azure'],
    metrics: {
      'Process Efficiency Increase': '30%',
      'Inventory Management Accuracy': '98%',
      'Deployment Time': '6 months',
    },
    outcome:
      'Streamlined operations and enhanced inventory accuracy, resulting in a 30% boost in efficiency.',
  },
  {
    title: 'E-commerce Automation Platform',
    slug: 'ecommerce-automation-platform',
    category: 'Enterprise Solutions',
    type: 'Enterprise Solutions',
    image: 'https://images.unsplash.com/photo-1533750349088-cd871a92f312?q=80&w=1024',
    highlight: '2x Faster Order Processing',
    description:
      'Built an e-commerce automation platform for streamlining order processing, inventory updates, and shipment tracking.',
    tech: ['Shopify API', 'React', 'GraphQL', 'Node.js'],
    metrics: {
      'Order Processing Speed': '2x faster',
      'Operational Cost Reduction': '25%',
      'Integration Time': '3 months',
    },
    outcome:
      'Enhanced customer satisfaction with faster order fulfillment and streamlined back-office operations.',
  },
  {
    title: 'Smart City IoT Platform',
    slug: 'smart-city-iot-platform',
    category: 'IoT & Edge Computing',
    type: 'IoT & Edge Computing',
    image: 'https://images.unsplash.com/photo-1558346490-a72e53ae2d4f?q=80&w=1024',
    highlight: '50,000+ Connected Devices',
    description:
      'Engineered a robust IoT platform managing 50,000+ connected devices for smart city infrastructure, optimizing energy use and traffic flow.',
    tech: ['Go', 'MQTT', 'Kubernetes', 'InfluxDB'],
    metrics: {
      'Connected Devices': '50,000+',
      'Message Throughput': '10K messages/sec',
      'Response Latency': '<50ms',
    },
    outcome:
      'Reduced citywide energy consumption by 30% while improving traffic flow and operational efficiency.',
  },
  {
    title: 'Industrial IoT Monitoring System',
    slug: 'industrial-iot-monitoring-system',
    category: 'IoT & Edge Computing',
    type: 'IoT & Edge Computing',
    image: 'https://images.unsplash.com/photo-1532298229144-0ec0c57515c7?q=80&w=1024',
    highlight: '25% Downtime Reduction',
    description:
      'Implemented an edge computing solution for real-time monitoring and predictive maintenance in manufacturing plants.',
    tech: ['C++', 'EdgeX Foundry', 'Apache Kafka', 'Grafana'],
    metrics: {
      'Downtime Reduction': '25%',
      'Monitoring Accuracy': '99%',
      'Data Processed Daily': '1TB',
    },
    outcome: 'Minimized downtime by 25% and increased operational visibility for factory managers.',
  },
  {
    title: 'NFT Marketplace',
    slug: 'nft-marketplace',
    category: 'Blockchain & Web3',
    type: 'Blockchain & Web3',
    image: '/images/nftMarketplace.jpeg',
    highlight: '500K+ NFTs Minted',
    description:
      'Developed an NFT marketplace platform for creators and collectors, integrating seamless minting and trading functionalities.',
    tech: ['Ethereum', 'IPFS', 'Next.js', 'GraphQL'],
    metrics: {
      'NFTs Minted': '500K+',
      'Active Users': '100K+',
      'Transaction Success Rate': '98%',
    },
    outcome:
      'Enabled creators to monetize their work effectively, resulting in 500K+ NFTs minted within a year.',
  },
  {
    title: 'Blockchain-Based Supply Chain',
    slug: 'blockchain-based-supply-chain',
    category: 'Blockchain & Web3',
    type: 'Blockchain & Web3',
    image: 'https://images.unsplash.com/photo-1593642532973-d31b6557fa68?q=80&w=1024',
    highlight: '10M+ Transactions Processed',
    description:
      'Built a blockchain-based solution for supply chain transparency and traceability across global networks.',
    tech: ['Hyperledger', 'Node.js', 'React', 'MongoDB'],
    metrics: {
      'Transactions Processed': '10M+',
      'Traceability Accuracy': '99%',
      'Operational Cost Savings': '20%',
    },
    outcome: 'Increased trust and transparency in supply chains, saving 20% in operational costs.',
  },
  {
    title: 'AI Diagnostics Platform',
    slug: 'ai-diagnostics-platform',
    category: 'Healthcare Tech',
    type: 'Healthcare Tech',
    image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?q=80&w=1024',
    highlight: '98% Accuracy',
    description:
      'Designed an AI-powered diagnostics platform for early disease detection using medical imaging and patient data analytics.',
    tech: ['PyTorch', 'TensorFlow', 'Python', 'Docker'],
    metrics: {
      Accuracy: '98%',
      'Processing Time': '<5 seconds per scan',
      'Cases Analyzed': '1.2M+',
    },
    outcome:
      'Improved early detection rates by 45%, reducing the time to diagnose critical conditions.',
  },
  {
    title: 'Telemedicine Platform',
    slug: 'telemedicine-platform',
    category: 'Healthcare Tech',
    type: 'Healthcare Tech',
    image: 'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?q=80&w=1024',
    highlight: '500K+ Consultations Conducted',
    description:
      'Built a secure telemedicine solution facilitating virtual consultations, prescription management, and patient monitoring.',
    tech: ['React', 'Node.js', 'PostgreSQL', 'AWS'],
    metrics: {
      'Consultations Conducted': '500K+',
      'System Uptime': '99.99%',
      'Patient Satisfaction Rate': '96%',
    },
    outcome:
      'Delivered a reliable telemedicine platform, increasing access to healthcare for remote patients.',
  },
  {
    title: 'Healthcare Data Integration System',
    slug: 'healthcare-data-integration-system',
    category: 'Healthcare Tech',
    type: 'Healthcare Tech',
    image: '/images/HealthcareData.jpeg',
    highlight: '85% Integration Coverage',
    description:
      'Developed a data integration system to unify patient records across hospitals, ensuring real-time updates and data security.',
    tech: ['FHIR', 'Python', 'AWS', 'Kubernetes'],
    metrics: {
      'Integration Coverage': '85% of records',
      'Data Update Latency': '<10ms',
      'System Uptime': '99.95%',
    },
    outcome:
      'Unified patient records across multiple healthcare providers, improving decision-making and care delivery.',
  },

  {
    title: 'Covidcitizen.org',
    slug: 'covidcitizen-org',
    category: 'Healthcare Tech',
    type: 'Crisis Response Platform',
    image:
      'https://pollinations.ai/p/A%20covid%20response%20platform%20helping%20citizens%20find%20resources%20and%20medical%20supplies?seed=covidcitizen&nologo=true',
    highlight: 'Scaled to 1M+ Users in 2 Days',
    description:
      'Single-handedly created and scaled the Covidcitizen.org system, which handled over a million users within two days of launch. The platform organized and structured critical Covid resources and medicines from on-ground suppliers, helping meet the needs of patients through digital channels.',
    tech: ['Node.js', 'React', 'AWS', 'MongoDB'],
    metrics: {
      'Users in First 2 Days': '1M+',
      Reference:
        'https://inc42.com/startups-vs-covid19/startupsvscovid19-15-founders-startup-employees-form-covidcitizens-org-to-fix-covid-resource-crunch-with-the-power-of-data/',
    },
    outcome:
      'Enabled rapid, verified access to Covid resources for millions, supporting patients and authorities during the crisis. [Read more](https://inc42.com/startups-vs-covid19/startupsvscovid19-15-founders-startup-employees-form-covidcitizens-org-to-fix-covid-resource-crunch-with-the-power-of-data/)',
  },
  {
    title: 'MTV Memory Karaoke',
    slug: 'mtv-memory-karaoke',
    category: 'Healthcare Tech',
    type: 'AI & Music Therapy',
    image:
      'https://pollinations.ai/p/An%20AI%20powered%20karaoke%20app%20helping%20alzheimers%20patients%20with%20memory%20through%20music?seed=mtvkaraoke&nologo=true',
    highlight: "AI Karaoke for Alzheimer's Patients",
    description:
      "Created a tool for Alzheimer's patients to help them remember information using an AI-powered karaoke app. The app leverages music and memory science to aid early Alzheimer's disease with interactive, personalized karaoke experiences.",
    tech: ['AI', 'React Native', 'Music Therapy', 'Node.js'],
    metrics: {
      Initiative: 'MTV Memory Karaoke',
      Reference:
        'https://campaignbriefasia.com/2021/10/01/mtv-launches-memory-karaoke-initiative-in-partnership-with-ogilvy-india-to-aid-early-alzheimers-disease-with-music/',
    },
    outcome:
      "Helped Alzheimer's patients and families use music as a tool for memory retention and emotional connection. [Read more](https://campaignbriefasia.com/2021/10/01/mtv-launches-memory-karaoke-initiative-in-partnership-with-ogilvy-india-to-aid-early-alzheimers-disease-with-music/)",
  },
];
